import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2, AlertCircle, CheckCircle2, Ear, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";

const MIC_CONSENT_KEY = "pnc_mic_consent_v1"; // "granted" after user chooses Continuar once

/**
 * Premium voice capture card. States: idle | recording | processing | success.
 * onReview(payload) opens the confirmation sheet.
 */
export default function VoiceCapture({ onReview, successFlash = 0 }) {
  const [state, setState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [text, setText] = useState("");
  const [supported, setSupported] = useState(true);
  const [showConsent, setShowConsent] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const { api } = useAuth();

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setSupported(false);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRef.current && mediaRef.current.state === "recording") mediaRef.current.stop();
    };
  }, []);

  useEffect(() => {
    if (successFlash > 0) {
      setState("success");
      const t = setTimeout(() => setState("idle"), 1800);
      return () => clearTimeout(t);
    }
  }, [successFlash]);

  const requestMic = () => {
    if (!supported) {
      toast.error("Seu navegador não suporta gravação. Use o campo abaixo.");
      return;
    }
    // In-app disclosure before native permission prompt (required by Play Store policy)
    const already = typeof localStorage !== "undefined" && localStorage.getItem(MIC_CONSENT_KEY) === "granted";
    if (!already) {
      setShowConsent(true);
      return;
    }
    startRecording();
  };

  const acceptConsent = () => {
    try { localStorage.setItem(MIC_CONSENT_KEY, "granted"); } catch {}
    setShowConsent(false);
    startRecording();
  };

  const dismissConsent = () => {
    setShowConsent(false);
  };

  const startRecording = async () => {
    if (!supported) {
      toast.error("Seu navegador não suporta gravação. Use o campo abaixo.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 800) {
          setState("idle");
          toast.error("Áudio muito curto. Segure e fale por alguns segundos.");
          return;
        }
        setState("processing");
        const fd = new FormData();
        fd.append("audio", blob, "voz.webm");
        try {
          const r = await api.post("/nlp/voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (!r.data.items || r.data.items.length === 0) {
            toast.error("Não entendi. Tente 'gasolina 50 reais'.");
            setState("idle");
          } else {
            onReview(r.data);
            setState("idle");
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Falha na transcrição");
          setState("idle");
        }
      };
      mediaRef.current = rec;
      rec.start();
      setState("recording");
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch {
      toast.error("Permita o microfone nas configurações do navegador.");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state === "recording") mediaRef.current.stop();
  };

  const submitText = async () => {
    const value = text.trim();
    if (!value) return;
    setState("processing");
    try {
      const r = await api.post("/nlp/parse-multi", { text: value });
      if (!r.data.items || r.data.items.length === 0) {
        toast.error("Não entendi. Tente 'café 15 reais'.");
        setState("idle");
      } else {
        onReview(r.data);
        setText("");
        setState("idle");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao interpretar");
      setState("idle");
    }
  };

  const recording = state === "recording";
  const processing = state === "processing";
  const success = state === "success";

  const statusLabel = recording
    ? "Ouvindo…"
    : processing
    ? "Reconhecendo sua fala"
    : success
    ? "Salvo com sucesso!"
    : "Fale sua despesa";

  const statusHint = recording
    ? `Fale naturalmente • ${elapsed}s • toque para parar`
    : processing
    ? "Interpretando o que você disse…"
    : success
    ? "Movimentações registradas no seu painel."
    : "Ex.: “gasolina 50 reais e café da manhã 20 reais”";

  return (
    <div data-testid="voice-capture" className="pnc-card p-6 sm:p-7 relative overflow-hidden">
      {/* Ambient glow */}
      <div
        className={`absolute -top-24 -right-20 w-64 h-64 rounded-full blur-3xl pointer-events-none transition-colors duration-500 ${
          recording ? "bg-rose-500/25" : success ? "bg-emerald-500/30" : "bg-emerald-500/15"
        }`}
      />
      <div
        className={`absolute -bottom-24 -left-16 w-56 h-56 rounded-full blur-3xl pointer-events-none transition-colors duration-500 ${
          recording ? "bg-rose-500/10" : "bg-sky-500/10"
        }`}
      />

      <div className="relative flex flex-col items-center text-center">
        {/* Kicker */}
        <p className="text-[10px] uppercase tracking-[0.24em] text-emerald-300 font-semibold mb-2 flex items-center gap-1.5">
          {recording ? <Ear className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
          <span>Comando de voz</span>
          <span className="w-6 h-px bg-emerald-400/40" />
        </p>

        {/* Headline */}
        <h3 data-testid="voice-status-label" className="font-display text-2xl sm:text-3xl font-bold text-white mb-1.5">
          {statusLabel}
        </h3>
        <p data-testid="voice-status-hint" className="text-xs sm:text-sm text-slate-400 mb-6 max-w-[320px] min-h-[40px]">
          {statusHint}
        </p>

        {/* Big mic button */}
        <button
          data-testid="mic-big-btn"
          onClick={recording ? stopRecording : requestMic}
          disabled={processing}
          aria-label={recording ? "Parar gravação" : "Iniciar gravação por voz"}
          className={`relative w-32 h-32 sm:w-36 sm:h-36 rounded-full flex items-center justify-center text-white transition-transform active:scale-95 ${
            recording
              ? "bg-gradient-to-br from-rose-400 to-rose-600 mic-pulse"
              : processing
              ? "bg-slate-700 cursor-wait"
              : success
              ? "bg-gradient-to-br from-emerald-300 to-emerald-600 scale-105"
              : "bg-gradient-to-br from-emerald-300 to-emerald-600 mic-glow hover:scale-[1.04]"
          }`}
        >
          {/* Inner ring */}
          <span className={`absolute inset-2 rounded-full border ${recording ? "border-white/25" : "border-white/20"} pointer-events-none`} />
          {processing ? (
            <Loader2 className="w-14 h-14 animate-spin" strokeWidth={2} />
          ) : success ? (
            <CheckCircle2 className="w-14 h-14 text-slate-900" strokeWidth={2.4} />
          ) : recording ? (
            <MicOff className="w-14 h-14" strokeWidth={2.2} />
          ) : (
            <Mic className="w-14 h-14 text-slate-900" strokeWidth={2.4} />
          )}
          {recording && (
            <span className="absolute -bottom-1 right-1 bg-slate-950 text-rose-300 border border-rose-500/40 text-[10px] font-mono-nums font-bold px-2 py-0.5 rounded-full">
              {elapsed}s
            </span>
          )}
        </button>

        <p className="text-[11px] text-slate-500 mt-5 max-w-[280px]">
          {recording
            ? "Toque de novo para parar a gravação"
            : "Toque uma vez, fale naturalmente e revise antes de salvar."}
        </p>

        {!supported && (
          <div className="mt-4 w-full flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Seu navegador não suporta gravação. Use o campo abaixo para digitar.</span>
          </div>
        )}

        {/* Text fallback */}
        <div className="w-full mt-6 flex items-center gap-2 p-1.5 rounded-2xl bg-slate-950/60 border border-white/10">
          <input
            data-testid="voice-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitText()}
            placeholder="ou digite: café 15 reais"
            disabled={recording || processing}
            className="flex-1 bg-transparent outline-none px-3 py-2 text-sm text-white placeholder:text-slate-500 min-w-0"
          />
          <button
            data-testid="voice-send-btn"
            onClick={submitText}
            disabled={recording || processing || !text.trim()}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 text-sm font-bold"
          >Enviar</button>
        </div>
      </div>

      {showConsent && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" data-testid="mic-consent-modal">
          <div className="pnc-card w-full max-w-md p-6 space-y-4 border-emerald-500/25">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 flex items-center justify-center">
                <Mic className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-lg font-bold text-white">Usar o microfone</h3>
                <p className="text-[11px] text-slate-400">Opcional — você pode registrar movimentações só por texto.</p>
              </div>
            </div>
            <ul className="text-sm text-slate-300 space-y-2 bg-emerald-500/5 border border-emerald-500/15 rounded-xl p-3">
              <li className="flex items-start gap-2"><Mic className="w-4 h-4 shrink-0 mt-0.5 text-emerald-300" /><span>O Põe na Conta usa o microfone <strong>somente quando você toca para gravar</strong> um lançamento por voz.</span></li>
              <li className="flex items-start gap-2"><Ear className="w-4 h-4 shrink-0 mt-0.5 text-emerald-300" /><span>O áudio é usado para <strong>reconhecer o comando falado</strong> e transformá-lo em uma movimentação (valor, categoria, descrição).</span></li>
              <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-emerald-300" /><span>O recurso é <strong>opcional</strong>. Você pode negar e continuar usando o aplicativo normalmente.</span></li>
            </ul>
            <p className="text-[11px] text-slate-500">
              Mais detalhes na <Link to="/politica-de-privacidade" className="text-emerald-300 underline">Política de Privacidade</Link>.
            </p>
            <div className="flex gap-2">
              <button
                data-testid="mic-consent-dismiss-btn"
                onClick={dismissConsent}
                className="flex-1 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-semibold"
              >Agora não</button>
              <button
                data-testid="mic-consent-continue-btn"
                onClick={acceptConsent}
                className="flex-1 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-900 text-sm font-bold"
              >Continuar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
